/**
 * Auth provider. Two modes:
 *
 *  - MOCK (VITE_USE_MOCK_API=true): no Auth0, no network. A static mock token is wired into the API
 *    client and the app renders as if authenticated. The whole portal demos + tests with zero creds.
 *  - REAL: the Auth0 SPA SDK (@auth0/auth0-react) does Authorization-Code + PKCE; the access token is
 *    fetched silently per request and attached as a Bearer by the API client.
 *
 * In both modes the API client's token getter is set exactly once, so nothing else imports Auth0.
 */

import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { setTokenGetter, type TokenGetter } from "@/api/client";
import { USE_MOCK_API, DEV_BYPASS_AUTH, getAuth0Config } from "@/lib/env";
import {
  classifyRenewalError,
  clearRenewalFailure,
  recordRenewalFailure,
  renewalSequence,
} from "./tokenRenewal";

/**
 * Sign-out is published through a tiny context so any screen (the user menu, the no-access screen) can
 * trigger it without importing Auth0. In MOCK mode there is no real session to end, so it's a no-op
 * that simply reloads to a clean state.
 */
interface AuthActions {
  signOut: () => void;
}

const AuthActionsContext = createContext<AuthActions>({
  signOut: () => {},
});

/** Trigger the Auth0 redirect logout (or a no-op reload in mock mode). */
export function useSignOut(): () => void {
  return useContext(AuthActionsContext).signOut;
}

interface AuthProviderProps {
  children: ReactNode;
  /** Shown while the auth layer initializes (Auth0 redirect handling / token getter wiring). */
  loading: ReactNode;
  /** Shown in REAL mode when the user is not authenticated (triggers login). */
  unauthenticated: (login: () => void) => ReactNode;
}

/** MOCK mode: wire a static token and render immediately. Sign-out just reloads (no session to end). */
function MockAuth({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    setTokenGetter(async () => "mock-access-token");
    setReady(true);
  }, []);
  if (!ready) return null;
  return (
    <AuthActionsContext.Provider
      value={{
        signOut: () => {
          if (typeof window !== "undefined") window.location.assign("/");
        },
      }}
    >
      {children}
    </AuthActionsContext.Provider>
  );
}

/**
 * Build the API client's token source around Auth0's silent renewal.
 *
 * Exported, and not an inline closure inside the effect below, because this function IS the producer
 * half of the transient-renewal recovery: `recordRenewalFailure(classifyRenewalError(err))` is the only
 * line in the app that writes the verdict every later 401 is judged by. As a closure it was unreachable
 * from every test in the repo — each one wires its own `setTokenGetter(async () => "test-token")` — so
 * deleting that line left the whole suite green while silently reverting the fix in production. Hoisting
 * it here makes it directly testable, and `RealAuthInner` hands this exact function to `setTokenGetter`,
 * so the function under test is the one that ships. (See authProvider.tokenGetter.test.ts.)
 */
export function makeTokenGetter(getAccessTokenSilently: () => Promise<string>): TokenGetter {
  return async () => {
    // Captured BEFORE the renewal starts so the clear below can tell "the failure I am retiring was
    // already there when I began" from "a DIFFERENT, overlapping renewal failed while I was in flight".
    // Only the first is mine to erase; erasing the second blinds a request that is still waiting for its
    // 401 (see the sequence comment in tokenRenewal).
    const seq = renewalSequence();
    try {
      const token = await getAccessTokenSilently();
      // A good token retires any earlier failure record — otherwise a transient blip from a moment
      // ago could still be sitting there to explain a LATER, unrelated 401 (see tokenRenewal).
      clearRenewalFailure(seq);
      return token;
    } catch (err) {
      // The getter's contract is `string | null` (four call sites in api/client), so the reason a
      // renewal failed cannot travel with the return value — it goes out-of-band instead. Without it
      // a Web Locks timeout, an offline blip or an Auth0 429 is indistinguishable from a revoked
      // refresh token, and the 401 that follows ejects the user with no retry.
      recordRenewalFailure(classifyRenewalError(err));
      return null; // a 401 from the API then surfaces normally; no silent swallow of data
    }
  };
}

/** REAL mode inner: wires Auth0's silent token getter, gates render on authentication. */
function RealAuthInner({ children, loading, unauthenticated }: AuthProviderProps) {
  const { isLoading, isAuthenticated, getAccessTokenSilently, loginWithRedirect, logout } =
    useAuth0();
  const wired = useRef(false);

  useEffect(() => {
    if (wired.current) return;
    wired.current = true;
    setTokenGetter(makeTokenGetter(getAccessTokenSilently));
  }, [getAccessTokenSilently]);

  if (isLoading) return <>{loading}</>;
  if (!isAuthenticated) {
    return <>{unauthenticated(() => void loginWithRedirect())}</>;
  }
  return (
    <AuthActionsContext.Provider
      value={{
        signOut: () => void logout({ logoutParams: { returnTo: window.location.origin } }),
      }}
    >
      {children}
    </AuthActionsContext.Provider>
  );
}

export function AuthProvider(props: AuthProviderProps) {
  // MockAuth (static token, no Auth0) when fully mocking OR when previewing a real backend with the
  // local-dev auth bypass. In both cases there is no Auth0 tenant to talk to.
  if (USE_MOCK_API || DEV_BYPASS_AUTH) {
    return <MockAuth>{props.children}</MockAuth>;
  }

  const { domain, clientId, audience } = getAuth0Config();
  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience,
      }}
      // localstorage (not memory) so the rotating refresh token SURVIVES a page reload. With "memory",
      // every refresh drops the in-memory token and — because browsers block third-party cookies — the
      // silent iframe re-auth against the Auth0 session fails, bouncing the user to the sign-in gate on
      // EVERY refresh. Token-at-rest exposure is mitigated by the enforced CSP (script-src 'self', no
      // unsafe-inline) which blocks the XSS an attacker would need to read it.
      cacheLocation="localstorage"
      useRefreshTokens
    >
      <RealAuthInner {...props} />
    </Auth0Provider>
  );
}
